import React, { useMemo } from 'react';
import startCase from 'lodash/startCase';
import { Link } from 'react-router-dom';
import { Button, IconButton } from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';

import DataGridDyl from '../../../components/DataGridDyl';
import { LevelText, useLevels } from '@dyl/systems/LevelsManager';

/**
 * @typedef Props
 * @property {(id: string) => void} removeItem
 */

/**
 * @param {Props & import('@mui/x-data-grid').DataGridProps} props
 */
export default function OutputComponentList({ removeItem, ...other }) {
    const columns = useMemo(() => {
        /** @type {import('@mui/x-data-grid').GridColDef[]} */
        let columns = [
            { headerName: 'Component ID', field: 'test_name', flex: 1, renderCell({ formattedValue, row }) {
                return (
                    <>
                        <ComponentEditButton component={row} />
                        {formattedValue}
                    </>
                );
            }
            },
            { headerName: 'Level', field: 'level_id', flex: 1, renderCell({ value }) {
                return <LevelText levelId={value} />
            }
            },
            { headerName: 'Skill', field: 'skill_id', flex: 1, valueFormatter({ value }) {
                return startCase(value);
            }
            },
        ];

        if (removeItem) {
            columns.push({ headerName: 'Actions', field: 'action_remove', flex: 1, renderCell({ row }) {
                return (
                    <Button
                        style={{ color: '#FF1A1A' }}
                        onClick={() => removeItem(row.test_id)}
                    >
                        Remove
                    </Button>
                );
            }
            })
        }

        // Disable sorting
        columns = columns.map(c => ({ ...c, sortable: false }));

        return columns;
    }, [removeItem]);

    return (
        <DataGridDyl
            columns={columns}
            getRowId={getRowId}
            localeText={lang}
            pageSize={100}
            hideFooter
            disableSelectionOnClick
            disableColumnMenu
            {...other}
        />
    )
}

const getRowId = row => row.test_id;

/** @type {Partial<import('@mui/x-data-grid').GridLocaleText>} */
const lang = {
    noRowsLabel: 'No components selected',
};

function ComponentEditButton({ component }) {
    const { globalLevel } = useLevels();

    const { skill_id, test_id: componentId } = component;

    const levelId = useMemo(() => {
        return component.level_id ?? globalLevel?.level_id;
    }, [component, globalLevel]);

    return (
        <IconButton
            component={Link}
            to={`/${globalLevel.label}/components/${skill_id}/${componentId}`}
            target='_blank'
            disabled={!levelId}
            size="large">
            <EditIcon fontSize='small' />
        </IconButton>
    );
}